import { useI18N, usePageTitle, useTheme } from "@sundae/react-hooks";
import { Button, Heading, Text } from "@sundae/ui-toolkit";
import { useWalletDetails } from "@sundae/wallet-react";
import { Suspense, lazy } from "react";
import ConnectWalletButton from "../../components/ConnectWalletButton";
import InformationTeaser from "../../components/InformationTeaser";
import LoadingComponent from "../../components/LoadingComponent/LoadingComponent";
import PageContent from "../../components/PageContent/PageContent";
import PositionTable from "../../components/YieldFarmingComponents/PositionTable/PositionTable";
import YFJumbotron from "../../components/YieldFarmingComponents/YFJumbotron/YFJumbotron";
import { joyrideSelectors } from "../../constants/joyride.constants";
import { getPageTitle } from "../../constants/page.constants";
import { testIds } from "../../constants/testIds.constants";
import { useYieldFarmingPositions } from "../../hooks/farms/useYieldFarmingPositions";
import useAppNavigate from "../../hooks/router/useAppNavigate";
import { useIsWalletConnected } from "../../hooks/useIsWalletConnected";
import { getImagePathByTheme } from "../../utils/images.utils";
import { PortfolioTab, externalRoutes, paths } from "../routes";
import GeneralStats from "./components/GeneralStats/GeneralStats";
import RewardsLeaderBoardTable from "./components/RewardsLeaderBoardTable/RewardsLeaderBoardTable";

const LazyPoolTable = lazy(
  () =>
    import(
      /* webpackChunkName: "PoolTable" */ "../../components/LiquidityComponents/PoolTable/PoolTable"
    )
);

const YieldFarming = () => {
  const { t } = useI18N("farms");
  const isWalletConnected = useIsWalletConnected();
  const navigate = useAppNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const { details } = useWalletDetails();
  const { mergedPosition } = useYieldFarmingPositions({
    beneficiary: [details?.stakingAddress, details?.mainAddress].sort(),
  });

  usePageTitle(getPageTitle(t("yieldFarming", { ns: "glossary" })));

  return (
    <PageContent>
      <div className="mx-auto flex max-w-6xl flex-col gap-12">
        {/* -------------------------------------------------------------------------------------------------
         * Jumbotron
         * -----------------------------------------------------------------------------------------------*/}
        <YFJumbotron />

        <div className="relative grid gap-4 py-8">
          <div
            className="absolute -left-12 -right-12 bottom-0 top-0 overflow-hidden rounded-3xl dark:opacity-75"
            style={{
              backgroundImage: "url(/static/images/noise.svg)",
            }}
          />

          <div className="relative mt-4 grid gap-12 md:grid-cols-2 lg:grid-cols-3">
            <GeneralStats />

            <div>
              {isWalletConnected ? (
                <div
                  className="flex flex-col gap-6"
                  data-guide-selector={joyrideSelectors.guides.yieldFarming.positions}
                >
                  <PositionTable hideButton title={t("landingPage.positionsTitle")} />

                  {mergedPosition?.value?.length ? (
                    <Button
                      className="ml-auto w-fit"
                      size="sm"
                      variant="neutral"
                      onClick={() =>
                        navigate({ to: paths.root, search: { tab: PortfolioTab.yieldFarming } })
                      }
                    >
                      {t("landingPage.manageYourPositions")}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-6 text-center">
                  <Text tag="div" weight="semibold">
                    {t("notFound.context.wallet.title", { ns: "not-found" })}{" "}
                    <Text tag="span">🔌</Text>
                  </Text>

                  <div className="flex flex-col items-center justify-center gap-4">
                    <Text tag="p" variant="muted" size="sm">
                      {t("landingPage.connectWalletToSeePositions")}
                    </Text>
                    <ConnectWalletButton className="w-fit" size="sm" />
                  </div>
                </div>
              )}
            </div>

            <RewardsLeaderBoardTable />
          </div>
        </div>

        <div
          className="relative z-10 flex flex-col gap-12"
          data-testid={testIds.yieldFarming.landingPage.topFarms}
          id={testIds.yieldFarming.landingPage.topFarms}
        >
          <div className="space-y-4">
            <Heading size="2xl" tag="h1" weight="semibold">
              {t("landingPage.tableTitle")}
            </Heading>
            <Suspense fallback={<LoadingComponent className="min-h-[350px]" />}>
              <LazyPoolTable
                columnsDisabled={{
                  change24: true,
                  estimation: false,
                  fees24: true,
                  price: true,
                }}
                defaultSortConfig={{ sortBy: "estimation", direction: "desc" }}
                fetchPoolsWithDelegation
                showCreatePool={false}
              />
            </Suspense>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-8">
          <InformationTeaser
            i18n={{
              copy: t("landingPage.footer.teaser.pools.copy"),
              cta: t("landingPage.footer.teaser.pools.cta"),
              title: t("landingPage.footer.teaser.pools.title"),
            }}
            image={{
              src: getImagePathByTheme(
                "/static/images/information-teaser/liquidity_pools",
                isDarkMode
              ),
              alt: t("liquidity.informationTeaser.pools", { ns: "alt-tags" }),
            }}
            isExternal
            route={externalRoutes.liquidity}
          />
          <InformationTeaser
            i18n={{
              copy: t("landingPage.footer.teaser.impermanentLoss.copy"),
              cta: t("landingPage.footer.teaser.impermanentLoss.cta"),
              title: t("landingPage.footer.teaser.impermanentLoss.title"),
            }}
            image={{
              src: getImagePathByTheme(
                "/static/images/information-teaser/impermanent_loss",
                isDarkMode
              ),
              alt: t("liquidity.informationTeaser.impermanentLoss", { ns: "alt-tags" }),
            }}
            isExternal
            route={externalRoutes.risksAndRewards}
          />
        </div>
      </div>
    </PageContent>
  );
};

export default YieldFarming;
